import { plural } from "../../model/commandeListFormat";
import { nextPage, pageLabel, previousPage } from "../../model/commandeListRules";

const VISUALLY_HIDDEN = { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 };

function RefreshButton({ isRefreshing, isMobile, onRefresh }) {
    return (
        <button
            className="commande-refresh-button"
            onClick={onRefresh}
            disabled={isRefreshing}
            aria-label={isRefreshing ? "Actualisation en cours" : "Actualiser"}
            aria-busy={isRefreshing}
            title="Actualiser (R)"
            style={{
                minHeight: "42px", padding: "0 16px", borderRadius: "10px",
                border: "1px solid #d8d2c6", background: "white",
                cursor: isRefreshing ? "wait" : "pointer", fontSize: "13px", color: "#122a26", fontWeight: 800,
                display: "flex", alignItems: "center", gap: "6px",
            }}
        >
            <svg className={`commande-refresh-icon${isRefreshing ? " is-spinning" : ""}`} aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
            {!isMobile && (isRefreshing ? "Actualisation…" : "Actualiser")}
        </button>
    );
}

function HeaderRow({ layout, totalElements, showStatut, searchContent, isRefreshing, onRefresh, onCreate }) {
    const { isMobile, isCompact } = layout;
    return (
        <div className="commande-header-row">
            <div className="commande-header-title">
                <h2 style={{ margin: 0, fontFamily: "Fraunces, Georgia, serif", fontSize: isCompact ? "22px" : "34px", lineHeight: 1.05, fontWeight: 700, color: "#122a26", letterSpacing: "-0.02em" }}>
                    Commandes reçues
                </h2>
                {showStatut && <span style={VISUALLY_HIDDEN}>STATUT</span>}
                <p style={{ margin: "6px 0 0", fontSize: "13px", color: "#5a6d68", fontWeight: 650 }}>
                    {totalElements} résultat{plural(totalElements)}
                </p>
            </div>

            {searchContent && <div className="commande-header-search" data-testid="commande-header-search">{searchContent}</div>}

            <RefreshButton isRefreshing={isRefreshing} isMobile={isMobile} onRefresh={onRefresh} />

            <button
                className="commande-create-button"
                onClick={onCreate}
                style={{
                    minHeight: "42px", padding: "0 18px", borderRadius: "10px",
                    border: "none", background: "#0c3b3c", color: "white",
                    cursor: "pointer", fontSize: "13px", fontWeight: 800,
                    whiteSpace: "nowrap",
                    boxShadow: "0 6px 16px rgba(12, 59, 60, 0.16)",
                }}
            >
                + {isMobile ? "Nouvelle" : "Nouvelle commande"}
            </button>
        </div>
    );
}

function CompactTools({ onOpenLabels, onOpenFilters, hasActiveFilters, activeFilterCount }) {
    if (!onOpenLabels && !onOpenFilters) return null;
    return (
        <div className="commande-mobile-tools" aria-label="Organisation des commandes">
            {onOpenLabels && (
                <button type="button" onClick={onOpenLabels}>
                    <span aria-hidden="true">☰</span>
                    Libellés
                </button>
            )}
            {onOpenFilters && (
                <button type="button" className={hasActiveFilters ? "is-active" : ""} onClick={onOpenFilters}>
                    <span aria-hidden="true">⌁</span>
                    Filtres{activeFilterCount > 0 ? ` · ${activeFilterCount}` : ""}
                </button>
            )}
        </div>
    );
}

function Pagination({ page, totalPages, setPage }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "auto" }}>
            <button type="button" aria-label="Page précédente" disabled={page <= 0} onClick={() => setPage(previousPage)}>
                ‹
            </button>
            <span style={{ color: "#5a6d68", fontSize: "12px", fontWeight: 700 }}>
                {pageLabel(page, totalPages)}
            </span>
            <button
                type="button"
                aria-label="Page suivante"
                disabled={totalPages <= 1 || page >= totalPages - 1}
                onClick={() => setPage(nextPage(totalPages))}
            >
                ›
            </button>
        </div>
    );
}

// En-tête de la liste : titre, recherche, actualisation, création, outils compacts et pagination.
export function CommandeListHeader({ layout, header, toolbar, pagination }) {
    const { isCompact } = layout;
    const hasCompactTools = isCompact && (toolbar.onOpenLabels || toolbar.onOpenFilters);
    const showToolbar = toolbar.filtersContent || hasCompactTools || pagination.showPagination;
    return (
        <div style={{ padding: isCompact ? "12px 16px 10px" : "22px 28px 14px", background: "white", borderBottom: "1px solid #d8d2c6", flexShrink: 0 }}>
            <HeaderRow layout={layout} {...header} />

            {showToolbar && (
                <div className="commande-pagination-toolbar" data-testid="commande-pagination-toolbar">
                    {toolbar.filtersContent}
                    {hasCompactTools && <CompactTools {...toolbar} />}
                    {pagination.showPagination && <Pagination {...pagination} />}
                </div>
            )}
        </div>
    );
}
